/* eslint-disable @typescript-eslint/no-explicit-any -- react-hook-form useFieldArray name types require any */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { reflectionApi } from '@/services/learningManagementApi';
import { skillApi } from '@/services/skillKnowledgeApi';
import { useEntity, useMutation } from '@/hooks';
import { Button } from '@/components/ui/Button';
import { LoadingState, ErrorState } from '@/components/common';
import { Input } from '@/components/ui/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { ArrowLeft, Save, Plus, Trash2 } from 'lucide-react';
import { Label } from '@/components/ui/label';
import type {
  Reflection,
  ReflectionRequest,
  ReflectionSkillLevelImprovement,
  DeliveryFrequency,
  GenerationSource,
} from '@/types/learningManagement';

const skillResourceSchema = z.object({
  resourceId: z.string().optional(),
  resourceType: z.string().optional(),
  resourceUrl: z.string().optional(),
  resourceTitle: z.string().optional(),
});

const reflectionSchema = z.object({
  skillId: z.string().min(1, 'Skill ID is required'), // Required by API
  timeInvestedHours: z.number().min(0).optional(),
  improvementAreas: z.array(z.string()),
  nextWeekSkillGoals: z.array(z.string()),
  resourcesUsed: z.array(skillResourceSchema),
  practicalApplications: z.array(z.string()),
  skillLevelImprovement: z.enum(['SIGNIFICANT_IMPROVEMENT', 'MODERATE_IMPROVEMENT', 'SLIGHT_IMPROVEMENT', 'NO_CHANGE', 'SLIGHT_DECLINE', 'NEED_MORE_FOCUS']).optional(),
  reflectionNotes: z.string().max(5000).optional(),
  reflectionGenerated: z.enum(['SYSTEM_DEFINED', 'AI_GENERATED', 'USER_CUSTOMIZED']),
  deliveryChannels: z.array(z.string()),
  deliveryFrequency: z.enum(['ONCE', 'DAILY', 'WEEKLY', 'BIWEEKLY', 'MONTHLY', 'ON_TRIGGER', 'CUSTOM']).optional(),
  targetAudiences: z.array(z.string()),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

type ReflectionFormData = z.infer<typeof reflectionSchema>;

const SKILL_LEVEL_IMPROVEMENTS: ReflectionSkillLevelImprovement[] = ['SIGNIFICANT_IMPROVEMENT', 'MODERATE_IMPROVEMENT', 'SLIGHT_IMPROVEMENT', 'NO_CHANGE', 'SLIGHT_DECLINE', 'NEED_MORE_FOCUS'];
const DELIVERY_FREQUENCIES: DeliveryFrequency[] = ['ONCE', 'DAILY', 'WEEKLY', 'BIWEEKLY', 'MONTHLY', 'ON_TRIGGER', 'CUSTOM'];
const GENERATION_SOURCES: GenerationSource[] = ['SYSTEM_DEFINED', 'AI_GENERATED', 'USER_CUSTOMIZED'];

export const ReflectionFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [skills, setSkills] = useState<Array<{ id?: string; name: string }>>([]);

  const form = useForm<ReflectionFormData>({
    resolver: zodResolver(reflectionSchema),
    defaultValues: {
      skillId: '',
      timeInvestedHours: undefined,
      improvementAreas: [],
      nextWeekSkillGoals: [],
      resourcesUsed: [],
      practicalApplications: [],
      skillLevelImprovement: undefined,
      reflectionNotes: '',
      reflectionGenerated: 'USER_CUSTOMIZED',
      deliveryChannels: [],
      deliveryFrequency: 'ONCE',
      targetAudiences: [],
    },
  });

  const { fields: improvementFields, append: appendImprovement, remove: removeImprovement } = useFieldArray({
    control: form.control,
    name: 'improvementAreas' as any,
  });

  const { fields: goalFields, append: appendGoal, remove: removeGoal } = useFieldArray({
    control: form.control,
    name: 'nextWeekSkillGoals' as any,
  });

  const { fields: applicationFields, append: appendApplication, remove: removeApplication } = useFieldArray({
    control: form.control,
    name: 'practicalApplications' as any,
  });

  useEffect(() => {
    skillApi.list({ page: 0, size: 1000 }).then((response) => {
      setSkills(response.content || []);
    }).catch((error) => {
      console.error('Error loading skills:', error);
    });
  }, []);

  // Fetch reflection data for edit mode
  const {
    data: _reflection,
    isLoading: isLoadingReflection,
    error: reflectionError,
  } = useEntity<Reflection>({
    fetchFn: (reflectionId: string) => reflectionApi.getById(reflectionId),
    entityId: id,
    entityName: 'reflection',
    enabled: isEdit && !!id,
    onSuccess: (data) => {
      form.reset({
        skillId: data.skillId || '',
        timeInvestedHours: data.timeInvestedHours,
        improvementAreas: data.improvementAreas || [],
        nextWeekSkillGoals: data.nextWeekSkillGoals || [],
        resourcesUsed: data.resourcesUsed || [],
        practicalApplications: data.practicalApplications || [],
        skillLevelImprovement: data.skillLevelImprovement,
        reflectionNotes: data.reflectionNotes || '',
        reflectionGenerated: data.reflectionGenerated,
        deliveryChannels: data.deliveryChannels || [],
        deliveryFrequency: data.deliveryFrequency,
        targetAudiences: data.targetAudiences || [],
      } as any);
    },
    onError: () => {
      navigate('/admin/reflections');
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: ReflectionFormData) => {
      const payload: ReflectionRequest = {
        ...data,
        skillId: data.skillId, // Required, no conversion needed
        improvementAreas: data.improvementAreas || [],
        nextWeekSkillGoals: data.nextWeekSkillGoals || [],
        resourcesUsed: data.resourcesUsed || [],
        practicalApplications: data.practicalApplications || [],
        deliveryChannels: data.deliveryChannels || [],
        targetAudiences: data.targetAudiences || [],
      } as ReflectionRequest;
      return await reflectionApi.create(payload);
    },
    entityName: 'reflection',
    operation: 'create',
    onSuccess: () => {
      navigate('/admin/reflections');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: ReflectionFormData) => {
      if (!id) throw new Error('Reflection ID is required');
      const payload: ReflectionRequest = {
        ...data,
        skillId: data.skillId, // Required, no conversion needed
        improvementAreas: data.improvementAreas || [],
        nextWeekSkillGoals: data.nextWeekSkillGoals || [],
        resourcesUsed: data.resourcesUsed || [],
        practicalApplications: data.practicalApplications || [],
        deliveryChannels: data.deliveryChannels || [],
        targetAudiences: data.targetAudiences || [],
      } as ReflectionRequest;
      return await reflectionApi.update(id, payload);
    },
    entityName: 'reflection',
    operation: 'update',
    onSuccess: () => {
      navigate('/admin/reflections');
    },
  });

  const onSubmit = async (data: ReflectionFormData) => {
    if (isEdit) {
      await updateMutation.mutateAsync(data);
    } else {
      await createMutation.mutateAsync(data);
    }
  };

  const isLoading = isLoadingReflection;
  const mutation = isEdit ? updateMutation : createMutation;

  // In edit mode, show loading state while fetching
  if (isEdit && isLoading) {
    return <LoadingState message="Loading reflection..." />;
  }

  // Show error if there's an error (only after loading completes)
  if (isEdit && reflectionError) {
    return <ErrorState error={reflectionError} onRetry={() => window.location.reload()} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/reflections">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {isEdit ? 'Edit Reflection' : 'Create Reflection'}
            </h1>
            <p className="text-muted-foreground mt-2">
              {isEdit ? 'Update reflection information' : 'Create a new reflection'}
            </p>
          </div>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>Enter the basic details for the reflection</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="skillId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Skill *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select skill (required)" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {skills.map((skill) => (
                          <SelectItem key={skill.id} value={skill.id || ''}>
                            {skill.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="timeInvestedHours"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Time Invested (Hours)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={0}
                          step="0.1"
                          placeholder="Enter hours"
                          {...field}
                          onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : undefined)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="skillLevelImprovement"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Skill Level Improvement</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select improvement level" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {SKILL_LEVEL_IMPROVEMENTS.map((level) => (
                            <SelectItem key={level} value={level}>
                              {level.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="reflectionNotes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Reflection Notes</FormLabel>
                    <FormControl>
                      <textarea
                        className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        placeholder="Enter reflection notes"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-2">
                <Label>Improvement Areas</Label>
                <div className="space-y-2">
                  {improvementFields.map((field, index) => (
                    <div key={field.id} className="flex gap-2">
                      <Input
                        placeholder="Enter improvement area"
                        value={form.watch(`improvementAreas.${index}`) || ''}
                        onChange={(e) => {
                          const current = form.getValues('improvementAreas') || [];
                          current[index] = e.target.value;
                          form.setValue('improvementAreas', current);
                        }}
                      />
                      <Button type="button" variant="outline" size="icon" onClick={() => removeImprovement(index)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={() => appendImprovement('')}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Improvement Area
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Next Week Skill Goals</Label>
                <div className="space-y-2">
                  {goalFields.map((field, index) => (
                    <div key={field.id} className="flex gap-2">
                      <Input
                        placeholder="Enter skill goal"
                        value={form.watch(`nextWeekSkillGoals.${index}`) || ''}
                        onChange={(e) => {
                          const current = form.getValues('nextWeekSkillGoals') || [];
                          current[index] = e.target.value;
                          form.setValue('nextWeekSkillGoals', current);
                        }}
                      />
                      <Button type="button" variant="outline" size="icon" onClick={() => removeGoal(index)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={() => appendGoal('')}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Skill Goal
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Practical Applications</Label>
                <div className="space-y-2">
                  {applicationFields.map((field, index) => (
                    <div key={field.id} className="flex gap-2">
                      <Input
                        placeholder="Enter practical application"
                        value={form.watch(`practicalApplications.${index}`) || ''}
                        onChange={(e) => {
                          const current = form.getValues('practicalApplications') || [];
                          current[index] = e.target.value;
                          form.setValue('practicalApplications', current);
                        }}
                      />
                      <Button type="button" variant="outline" size="icon" onClick={() => removeApplication(index)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={() => appendApplication('')}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Practical Application
                  </Button>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="deliveryFrequency"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Delivery Frequency</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select frequency" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {DELIVERY_FREQUENCIES.map((freq) => (
                            <SelectItem key={freq} value={freq}>
                              {freq.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="reflectionGenerated"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Generation Source *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select generation source" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {GENERATION_SOURCES.map((source) => (
                            <SelectItem key={source} value={source}>
                              {source.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" asChild>
              <Link to="/admin/reflections">Cancel</Link>
            </Button>
            <Button type="submit" disabled={mutation.isLoading}>
              <Save className="mr-2 h-4 w-4" />
              {mutation.isLoading ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default ReflectionFormPage;







